import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { READ_ONLY_ROLE } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestTypes/exceptions/configs'
import { checkIsFlaggingEffectSetIfNeeded } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestTypes/exceptions/helpers/checkFalggingEffect'
import { checkIsThresholdConfirmedIfNeeded } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestTypes/exceptions/helpers/checkThresholdConfirmation'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button, IButtonProps } from 'core-components/library/button'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { Controller } from 'core-components/library/validation'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { calculateFileLink } from 'core/classes/helpers/saveFiles'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { collectedDataApi, CollectedDataCacheKey } from 'onboarding/api/collectedDataApi'
import { showError, warning } from 'onboarding/classes/services/notification'
import { getNoAudienceAccess } from 'onboarding/components/ui/audienceFilter/helpers/getNoAudienceAccess'
import { FilePanel, IFilePanelProps } from 'onboarding/components/ui/filePanel/FilePanel'
import { TDeleteFileMeta } from 'onboarding/components/ui/filePanel/types'
import { useSaveFormStatus } from 'onboarding/components/widgets/public/admin/exceptionsReasons/hooks/useSaveFormStatus'
import { useParentRequest } from 'onboarding/hooks/useParentRequest'
import { useRequest } from 'onboarding/hooks/useRequest'
import { collectedDataService } from 'onboarding/services/collectedDataService'
import { exceptionRequestService } from 'onboarding/services/exceptionRequestService'
import { fileService } from 'onboarding/services/fileService'

import {
  ExceptionWelcomeForm,
  IExceptionWelcomeFormProps,
} from '../../../../requestTypes/exceptions/ExceptionWelcomeForm'
import { getEmptyExceptionParams } from '../../../../requestTypes/exceptions/helpers/getInitException'
import { parseExceptionReason } from '../../../../requestTypes/exceptions/helpers/parseExceptionReason'
import { IExceptionParams, IExceptionProof } from '../../../../requestTypes/exceptions/models'
import { ICollectorData } from '../ICollectorData'

import styles from './ExceptionForm.module.scss'

interface IProps {
  requestId: string
  flexWidth: string
  audienceSettings: IAudienceCondition
  disabled?: boolean
}

const FORM_FILES_FIELD = 'Value'

const unifyFiles = (items: ICollectorData[]) => {
  const filesUID = new Set<string>()
  const result: ICollectorData[] = []

  items.forEach((i) => {
    if (!(i.UID && filesUID.has(i.UID))) {
      result.push(i)
      i.UID && filesUID.add(i.UID)
    }
  })

  return result
}

/** todo: refactor:
 * 1. states, handlers
 * 2. create and migrate to service-onboarding endpoints
 * @param props
 * @constructor
 */
export const ExceptionForm = (props: IProps) => {
  const user = useSelector(selectUser)
  const { audienceSettings } = props
  const [collectedDataItems, setCollectedDataItems] = useState<IObjectAny[]>([])
  const [excParams, setExcParams] = useState<IExceptionParams>(getEmptyExceptionParams)
  const [savedExceptionData, setSavedExceptionData] =
    useState<IExceptionParams>(getEmptyExceptionParams)
  const [isFormFetching, setIsFormFetching] = useState(true)
  const [isFormSaving, setIsFormSaving] = useState(false)
  const [isFileDeleting, setIsFileDeleting] = useState(false)
  const { req, reqWip } = useRequest(props.requestId)
  const { parentRequest, parentRequestWip } = useParentRequest(req.Parent)
  const request = useMemo(() => parentRequest ?? req, [req, parentRequest])

  const resultAudienceSettings = useMemo(() => {
    if (user.roleIds.includes(READ_ONLY_ROLE)) {
      return getNoAudienceAccess()
    }

    let roles = audienceSettings.UserRoles
    if (roles?.length) {
      roles = roles.filter((role) => role !== READ_ONLY_ROLE)
    }

    return { ...audienceSettings, UserRoles: roles }
  }, [audienceSettings, user.roleIds])

  const { isSaveEnabled, disableSaveMessage } = useSaveFormStatus({
    previousState: savedExceptionData,
    nextState: excParams,
    isFormValid:
      Boolean(excParams.ExceptionReason.length) &&
      checkIsFlaggingEffectSetIfNeeded(excParams.IsCycleEffect, excParams.FlaggingEffect) &&
      checkIsThresholdConfirmedIfNeeded(
        request.Type,
        excParams.ExcCode,
        excParams.IsExceptionRequestPartnerThresholdConfirmed
      ),
  })

  const requestFilesSource = CrudStore.get(CrudStoreItemOnboarding.RequestFiles, 'filesList')

  const handleCollectedData = async (data: ICollectorData[]) => {
    setCollectedDataItems(data)
    const localExceptionParams = data.reduce((acc, cur) => {
      if (cur.Type !== 'file') {
        return { ...acc, [cur.Field]: cur.Value }
      } else {
        return {
          ...acc,
          [cur.Field]: acc[cur.Field] ? [...acc[cur.Field], cur] : [cur],
        }
      }
    }, {} as IObjectAny)

    const savedExceptionCode = (request.Props && JSON.parse(request.Props).MainParameter) || ''
    const exception = savedExceptionCode
      ? await exceptionRequestService.getExceptionByCode(req.Type, savedExceptionCode)
      : null

    const {
      ExceptionReason: localExceptionReason,
      ExceptionCategoryGUID: localExceptionCategoryGUID,
      ExceptionProofs: localExceptionProofs,
      ...otherLocalExceptionParams
    } = localExceptionParams
    const resultPayload: IExceptionParams = {
      ...excParams,
      ...otherLocalExceptionParams,
      ExceptionReason: localExceptionReason ? parseExceptionReason(localExceptionReason) : [],
      ExceptionCategoryGUID: localExceptionCategoryGUID
        ? parseExceptionReason(localExceptionCategoryGUID)
        : [],
      ExcCode: savedExceptionCode,
      ExcDomain: exception?.Domain || '',
      Group: exception?.Group || '',
      IsMultiSelect: Boolean(exception?.IsMultiSelect),
      IsCycleEffect: Boolean(exception?.IsCycleEffect),
      IsExceptionRequestPartnerThresholdConfirmed: Boolean(
        otherLocalExceptionParams.IsExceptionRequestPartnerThresholdConfirmed
      ),
      ExceptionProofs: localExceptionProofs
        ? localExceptionProofs.map((el: ICollectorData) => ({
            Value: el.Value,
            Type: 'file',
            Field: 'ExceptionProofs',
            EntryId: el.EntryId,
            file: null,
            UID: el.UID,
          }))
        : [],
    }
    setSavedExceptionData(resultPayload)
    setExcParams(resultPayload)
  }

  const onSave = async () => {
    if (!isSaveEnabled) {
      warning(disableSaveMessage)
    }
    setIsFormSaving(true)

    const files = excParams.ExceptionProofs || []
    const fieldsPayloadToSave = {
      BusinessJustification: excParams.BusinessJustification,
      FlaggingEffect: excParams.FlaggingEffect || undefined,
      ExceptionReason: excParams.ExceptionReason.length
        ? JSON.stringify(excParams.ExceptionReason)
        : undefined,
      ExceptionCategoryGUID: excParams.ExceptionCategoryGUID.length
        ? JSON.stringify(excParams.ExceptionCategoryGUID)
        : undefined,
      IsExceptionRequestPartnerThresholdConfirmed:
        excParams.IsExceptionRequestPartnerThresholdConfirmed || undefined,
    }
    const fields = Object.keys(fieldsPayloadToSave)
      .filter((key) => excParams[key] !== undefined)
      .reduce((acc, key) => {
        const found = collectedDataItems.find((el) => el.Field === key)
        if (found) {
          return [...acc, { ...found, Value: fieldsPayloadToSave[key] }]
        }

        return [
          ...acc,
          {
            Field: key,
            RequestId: request.EntryId,
            Value: fieldsPayloadToSave[key],
          },
        ]
      }, [] as IObjectAny[])

    const fieldsToUpdate = fields.filter((el) => el.EntryId) as ICollectorData[]
    const fieldsToCreate = fields.filter((el) => !el.EntryId && el.Value) as ICollectorData[]

    const filesToCreate = files.filter((el) => !el.UID)

    const [createdFields, updatedFields, createdFiles] = await Promise.all([
      fieldsToCreate.length ? collectedDataService.create(fieldsToCreate) : [],
      fieldsToUpdate.length ? collectedDataService.update(fieldsToUpdate) : [],
      filesToCreate.length ? createFiles(filesToCreate) : [],
    ])
    await handleCollectedData([
      ...createdFields,
      ...updatedFields,
      ...unifyFiles([
        ...(files.filter((el) => Boolean(el.EntryId && el.UID)) as unknown as ICollectorData[]),
        ...createdFiles,
      ]),
    ])

    setIsFormSaving(false)
  }

  const setFiles = (files: IExceptionProof[]) => {
    onChange({
      ExceptionProofs: files,
    })
  }

  const createFiles = async (filesWithoutEntryId: IExceptionProof[]) => {
    const dataToSave = filesWithoutEntryId.map(({ Value, Type, Field, file }) => {
      return {
        Value,
        Type,
        Field,
        RequestId: request.EntryId,
        file,
      }
    })

    const source = collectedDataService.getCrud(CollectedDataCacheKey.ExceptionForm)
    const result = await fileService.saveInDBAndServer(
      {
        formId: collectedDataApi.FORM_ID,
        formCode: collectedDataApi.FORM_CODE,
        fieldName: FORM_FILES_FIELD,
      },
      dataToSave,
      source
    )
    if (Array.isArray(result)) {
      requestFilesSource.removeQueries()
    }
    const { items } = await source.fetch()

    return items as ICollectorData[]
  }

  const onDeleteFile = async ({ entryId, uid, name }: TDeleteFileMeta) => {
    if (!uid) {
      setExcParams((prevState) => ({
        ...prevState,
        ExceptionProofs: prevState.ExceptionProofs.filter((_, index) => index !== entryId),
      }))

      return
    }

    setIsFileDeleting(true)

    await fileService.deleteFromDBAndServer(
      {
        uid,
        entryId,
        formId: collectedDataApi.FORM_ID,
        formCode: collectedDataApi.FORM_CODE,
        field: FORM_FILES_FIELD,
        fileName: name,
      },
      collectedDataService.getCrud(CollectedDataCacheKey.ExceptionForm)
    )
    setExcParams((prevState) => ({
      ...prevState,
      ExceptionProofs: prevState.ExceptionProofs.filter((el) => el.UID !== uid),
    }))
    requestFilesSource.removeQueries()

    setIsFileDeleting(false)
  }

  const onChange = useCallback(
    (value: Partial<IExceptionParams>) =>
      setExcParams((prevState) => ({
        ...prevState,
        ...value,
      })),
    []
  )

  useEffect(() => {
    const initCollectedDataFields = async () => {
      try {
        setIsFormFetching(true)
        const { items, error } = await collectedDataService.fetchManyExceptionFields(
          request.EntryId,
          CollectedDataCacheKey.ExceptionForm
        )
        if (error) {
          throw new Error(error)
        }

        await handleCollectedData(items)
      } catch (e: unknown) {
        showError('Failed to retrieve form data')
        Logger.error(e)
      } finally {
        setIsFormFetching(false)
      }
    }

    initCollectedDataFields()
  }, [request.EntryId])

  return isFormFetching || isFormSaving || reqWip || parentRequestWip ? (
    <Loader />
  ) : (
    <Controller>
      <VisualAudienceExtended<IExceptionWelcomeFormProps>
        component={ExceptionWelcomeForm}
        write={resultAudienceSettings}
        formData={excParams}
        serverFormData={savedExceptionData}
        onChange={onChange}
        type={request.Type}
        disabled={props.disabled}
        filePanel={
          <FilePanelController
            exception={excParams}
            setFiles={setFiles}
            onDelete={onDeleteFile}
            audienceSettings={resultAudienceSettings}
            isDeleting={isFileDeleting}
            disabled={props.disabled}
          />
        }
      />
      <div className={styles.btnWrapper}>
        <Tooltip
          tipContent={() => <span>{disableSaveMessage}</span>}
          disabled={props.disabled || isSaveEnabled}
        >
          <VisualAudienceExtended<IButtonProps>
            write={resultAudienceSettings}
            component={Button}
            onClick={onSave}
            disabled={props.disabled || !isSaveEnabled}
          >
            Save
          </VisualAudienceExtended>
        </Tooltip>
      </div>
    </Controller>
  )
}

interface IFilePanelControllerProps {
  exception: IExceptionParams
  setFiles: (items: IExceptionProof[]) => void
  onDelete: (item: TDeleteFileMeta) => void
  audienceSettings?: IAudienceCondition
  isDeleting?: boolean
  disabled?: boolean
}

const FilePanelController = ({
  exception,
  setFiles,
  onDelete,
  audienceSettings,
  isDeleting,
  disabled,
}: IFilePanelControllerProps) => {
  return (
    <div className={styles.filePanelWrapper}>
      <LoaderOverlay show={isDeleting ?? false} />
      <VisualAudienceExtended<IFilePanelProps>
        component={FilePanel}
        write={audienceSettings}
        value={(exception.ExceptionProofs ?? []).map((item, index) => ({
          entryId: item.EntryId || index,
          name: item.Value,
          uid: item.UID,
          link:
            item.EntryId && item.UID
              ? calculateFileLink({
                  formId: collectedDataApi.FORM_ID,
                  fieldName: FORM_FILES_FIELD,
                  fileName: item.Value,
                  entryId: item.EntryId,
                  UID: item.UID,
                })
              : undefined,
        }))}
        onUpload={(items) => {
          const filesToSave = items.map((item) => ({
            file: item.file,
            Value: item.file.name,
            Field: 'ExceptionProofs',
            Type: 'file',
          }))
          setFiles(
            exception.ExceptionProofs ? exception.ExceptionProofs.concat(filesToSave) : filesToSave
          )
        }}
        onDelete={onDelete}
        isMultiFiles={true}
        disabled={disabled}
      />
    </div>
  )
}
